<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ route.params.id ? 'Editar Opção da Questão' : 'Nova Opção da Questão' }}
      </h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>

    <!-- Seção A: Seleção da Questão (Categoria / Bloco / Questão concatenados) -->
    <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarOpcao" @cancel="voltar">
      <FormSection title="Informações da Questão">
        <div class="mb-3">
          <SelectField
            id="id_questao"
            v-model="opcao.id_questao"
            :options="questoesOptions"
            placeholder="Selecione a Questão"
            label="Questão <span class='required'>*</span>"
            required
            @update:modelValue="onQuestaoChange"
          />
          <div v-if="errors.id_questao" class="error-message">{{ errors.id_questao }}</div>
        </div>
      </FormSection>

      <!-- Seção B: Dados da Opção -->
      <FormSection title="Resposta e Opção">
        <!-- Campo Resposta -->
        <div class="form-group">
          <label for="resposta" class="form-label">
            Resposta <span class="required">*</span>
          </label>
          <template v-if="infoQuestao.tipo_dado === 'Booleano'">
            <div class="form-switch d-flex align-items-center gap-2">
              <input type="checkbox" id="resposta" v-model="opcao.resposta" class="form-check-input" />
              <label for="resposta" class="form-check-label">
                {{ opcao.resposta ? "Sim" : "Não" }}
              </label>
            </div>
          </template>
          <template v-else-if="infoQuestao.tipo_dado === 'Inteiro'">
            <InputField
              id="resposta"
              type="number"
              v-model.number="opcao.resposta"
              placeholder="Digite a resposta"
              required
              width="250px"
              :class="{ 'error-input': errors.resposta }"
            />
          </template>
          
          <template v-else>
            <InputField
              id="resposta"
              type="text"
              v-model="opcao.resposta"
              placeholder="Digite a resposta"
              :label="''"
              required
              width="250px"
              :class="{ 'error-input': errors.resposta }"
            />
          </template>
          <div v-if="errors.resposta" class="error-message">{{ errors.resposta }}</div>
        </div>

        <!-- Campo Pontuação (só para Numérico) -->
        <div v-if="infoQuestao.tipo_dado === 'Numérico'" class="form-group">
          <InputField
            id="pontuacao"
            v-model="pontuacaoInput"
            placeholder="Digite a pontuação"
            label="Pontuação <span class='required'>*</span>"
            required
            :class="{ 'error-input': errors.pontuacao }"
            width="120px"
          />
          <div v-if="errors.pontuacao" class="error-message">{{ errors.pontuacao }}</div>
        </div>

        <!-- Campo Correta (para Booleano, Lista ou Múltipla seleção) -->
        <div class="form-group" v-if="['Booleano','Lista','Múltipla seleção'].includes(infoQuestao.tipo_dado)">
          <SelectField
            id="correta"
            v-model="opcao.correta"
            :options="corretaOptions"
            placeholder="Selecione a opção correta"
            label="Correta? <span class='required'>*</span>"
            required
          />
          <div v-if="errors.correta" class="error-message">{{ errors.correta }}</div>
        </div>

        <!-- Campo Observação -->
        <div class="form-group">
          <InputField
            id="observacao"
            type="text"
            v-model="opcao.observacao"
            placeholder="Digite uma observação (opcional)"
            label="Observação"
            width="250px"
          />
        </div>
      </FormSection>
    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import InputField from "@/components/Layout/InputField.vue";
import SelectField from "@/components/Layout/SelectField.vue";
import FormSection from "@/components/Layout/FormSection.vue";
import BaseForm from "@/components/Layout/BaseForm.vue";
import axios from "@/services/axios";

export default {
  components: { InputField, SelectField, FormSection, BaseForm },
  setup() {
    const route = useRoute();
    const router = useRouter();

    // Carrega todas as questões para o select (concatenadas: Categoria / Bloco / Questão)
    const questoes = ref([]);
    const questoesOptions = computed(() =>
      questoes.value.map(q => ({
        value: q.id,
        text: `${q.categoria_nome} / ${q.bloco_nome} / ${q.questao}`
      }))
    );

    const fetchQuestoes = async () => {
      try {
        const response = await axios.get("/configuracoes/parametros-avaliacoes/questoes/todos");
        questoes.value = response.data.data.sort((a, b) => {
          const valorA = `${a.categoria_nome} / ${a.bloco_nome} / ${a.questao}`;
          const valorB = `${b.categoria_nome} / ${b.bloco_nome} / ${b.questao}`;
          return valorA.localeCompare(valorB);
        });
      } catch (error) {
        console.error("Erro ao carregar questões:", error);
      }
    };

    // Quando a questão é alterada no select, atualiza infoQuestao
    const onQuestaoChange = (newValue) => {
      const selected = questoes.value.find(q => q.id == newValue);
      if (selected) {
        Object.assign(infoQuestao, selected);
      }
    };

    // infoQuestao guarda os dados da questão selecionada (read-only)
    const infoQuestao = reactive({
      id: null,
      categoria_nome: "",
      bloco_nome: "",
      tipo_dado: "",
      questao: "",
    });

    // opcao guarda os dados da resposta/opção
    // Agora, o campo "correta" é uma string que pode assumir "Sim", "Não" ou "N/A"
    const opcao = reactive({
      id: null,
      id_questao: "", // Valor selecionado no select (deve ser 2, por exemplo)
      resposta: "",   // Valor da resposta
      correta: "",    // Inicialmente vazio; deverá ser preenchido com "Sim", "Não" ou "N/A"
      observacao: "",
    });

    // Opções para o campo "correta"
    const corretaOptions = [
      { value: "Sim", text: "Sim" },
      { value: "Não", text: "Não" },
      { value: "N/A", text: "N/A" },
    ];

    const errors = reactive({});
    const isSubmitting = ref(false);

    const pontuacaoInput = ref("");

    // Validação: campos obrigatórios visíveis
    const validateForm = () => {
      errors.id_questao = opcao.id_questao ? "" : "Selecione uma questão.";
      errors.resposta = opcao.resposta !== "" && opcao.resposta !== null ? "" : "Resposta é obrigatória.";
      // Validação para o campo "correta" quando visível
      if (['Booleano','Lista','Múltipla seleção'].includes(infoQuestao.tipo_dado)) {
        errors.correta = opcao.correta ? "" : "Selecione uma opção para 'Correta'.";
      }
      if (infoQuestao.tipo_dado === "Numérico") {
        errors.pontuacao = pontuacaoInput.value.trim() !== ""
          ? ""
          : "A pontuação é obrigatória para tipo Numérico.";
      } else {
        errors.pontuacao = "";
      }
    };

    const isFormValid = computed(() => {
      // precisa ter questão selecionada
      if (!opcao.id_questao) return false;

      // validação do campo “resposta”, todos exceto Booleano
      if (infoQuestao.tipo_dado !== "Booleano") {
        const resp = opcao.resposta?.toString().trim() || "";
        if (!resp) return false;
        // só valida número se for Inteiro
        if (infoQuestao.tipo_dado === "Inteiro" && isNaN(Number(resp))) return false;
      }

      // validação do campo “correta” apenas para Booleano
      if (infoQuestao.tipo_dado === "Booleano") {
        if (!opcao.correta) return false;
      }

      // validação do campo “pontuação” para tipo Numérico
      if (infoQuestao.tipo_dado === "Numérico") {
        const pt = pontuacaoInput.value?.toString().trim() || "";
        if (pt === "") return false;
      }

      return true;
    });

    // Prepara os dados para envio, utilizando o valor selecionado para "correta"
    const prepararDados = () => {
      let respostaValor = opcao.resposta;
      if (infoQuestao.tipo_dado === "Booleano") {
        respostaValor = opcao.resposta ? "Sim" : "Não";
      }
      const dados = {
        id_questao:  opcao.id_questao,
        opcao:       respostaValor,
        correta:     ['Booleano', 'Lista', 'Múltipla seleção'].includes(infoQuestao.tipo_dado)
                      ? opcao.correta
                      : "N/A",
        observacao:  opcao.observacao,
        pontuacao:   infoQuestao.tipo_dado === "Numérico"
                      ? parseFloat(pontuacaoInput.value.replace(",", ".")) || 0
                      : null
      };
      return dados;
    };

    const salvarOpcao = async () => {
      validateForm();
      if (!isFormValid.value) {
        console.error("O formulário contém erros:", errors);
        return;
      }
      if (isSubmitting.value) return;
      isSubmitting.value = true;
      try {
        const method = opcao.id ? "put" : "post";
        const url = opcao.id
          ? `/configuracoes/parametros-avaliacoes/opcoes/${opcao.id}`
          : "/configuracoes/parametros-avaliacoes/opcoes";
        const dados = prepararDados();
        await axios[method](url, dados);
        router.push("/configuracoes/parametros-avaliacoes/opcoes");
      } catch (error) {
        console.error("Erro ao salvar opção:", error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const voltar = () => {
      router.push("/configuracoes/parametros-avaliacoes/opcoes");
    };

    // Função para carregar a opção em modo edição
    const carregarOpcao = async (id) => {
      try {
        const response = await axios.get(`/configuracoes/parametros-avaliacoes/opcoes/${id}`);

        // Atualiza infoQuestao com os dados da questão associada
        Object.assign(infoQuestao, {
          id: response.data.id_questao,
          categoria_nome: response.data.categoria_nome,
          bloco_nome: response.data.bloco_nome,
          questao: response.data.questao,
          tipo_dado: response.data.tipo_dado,
        });

        // Atualiza os dados da opção, convertendo a resposta para booleano se necessário
        Object.assign(opcao, {
          id: response.data.id,
          id_questao: response.data.id_questao,
          resposta: infoQuestao.tipo_dado === "Booleano"
                      ? (response.data.opcao === "Sim")
                      : response.data.opcao,
          correta: response.data.correta,
          observacao: response.data.observacao,
        });

        // inicializa pontuação
        if (response.data.pontuacao != null) {
          const n = parseFloat(String(response.data.pontuacao).replace(",", "."));
          pontuacaoInput.value = !isNaN(n) 
            ? n.toFixed(2).replace(".", ",")
            : "";
        } else {
          pontuacaoInput.value = "";
        }
      } catch (error) {
        console.error("Erro ao carregar a opção:", error);
      }
    };

    watch(() => infoQuestao.tipo_dado, tipo => {
      if (tipo !== "Numérico") pontuacaoInput.value = "";
    });

    onMounted(async () => {
      await fetchQuestoes();
      // Se estiver editando, carregar a opção
      if (route.params.id) {
        await carregarOpcao(route.params.id);
      }
    });

    return {
      onQuestaoChange,
      questoesOptions,
      infoQuestao,
      opcao,
      corretaOptions,
      errors,
      isFormValid,
      isSubmitting,
      salvarOpcao,
      voltar,
      route,
      pontuacaoInput
    };
  },
};
</script>

<style scoped>
.card {
  max-width: 90%;
  margin: 0 auto;
}
.error-input {
  border-color: #dc3545;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
}
.larger-field {
  min-width: 180px;
}
</style>
